import React, { useState, useEffect } from "react";
import { DraggableLaborRow } from "./laborRow";
import { useSelector } from "react-redux";
import sortBy from "lodash/sortBy";
import Button from "react-bootstrap/Button";
import AddLaborCodeForm from "../forms/AddLaborCodeForm";
import { useParams, useNavigate } from "react-router-dom";
import { DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { calcuateLineItemTotal, laborItemsHeaders, laborItemsHeadersEditable } from "../../utils";
import { api } from "store/slices/api";
import { toast } from "react-toastify";

const QuoteFloorLabor = () => {
  const navigate = useNavigate();
  const { quoteId, floorId } = useParams();
  const [showAddLaborCode, setShowAddLaborCode] = useState(false);
  const { storeCcn, terrCcn, hasUpdateAccess } = useSelector((state) => state.loggedInUser);
  const { data: quote = {} } = api.useGetPriceQuoteQuery(quoteId);
  const { data: laborCodes = [], isFetching: isFetchingLaborCodes } = api.useGetFloorLaborCodesQuery(floorId);
  const [updateLaborCode] = api.useUpdateFloorLaborCodeMutation();

  // pre-loading labor codes so that they are immediately available when user opens the add labor code form
  api.useGetLaborCodesQuery();

  const altLShortcutListener = (e) => {
    if (e.altKey && e.code === "KeyL") {
      setShowAddLaborCode((showing) => !showing);
      e.preventDefault();
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", altLShortcutListener);
    return () => {
      window.removeEventListener("keydown", altLShortcutListener);
    };
  }, []);

  const canEdit =
    (quote.territory === terrCcn && quote.territory !== "800100") ||
    (storeCcn === quote.storeNumber && quote.punchoutPublished !== true);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 300, tolerance: 8 } }),
  );

  const sorted = sortBy(laborCodes, ["rank", "laborCode"]).map((lc, index) => ({ ...lc, rank: lc.rank ?? index + 1 }));
  const allSelected = laborCodes.length > 0 && !laborCodes.some((lc) => !lc.isTaxChecked);

  const handleSelectAllLabor = () => {
    const isTaxChecked = !allSelected;
    const taxRate = isTaxChecked ? quote.taxRate : 0;
    toast.promise(
      Promise.all(
        laborCodes.map(({ laborCodeId, price, quantity }) => {
          const totals = calcuateLineItemTotal({ price, quantity, taxRate });
          return updateLaborCode({ ...totals, laborCodeId, floorId, isTaxChecked }).unwrap();
        }),
      ),
      { success: "Changes saved!", pending: "Saving changes", error: "Something went wrong" },
    );
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pb-2">
        <div className="fw-bold">Labor Items</div>
        {canEdit && (
          <div>
            {hasUpdateAccess && (
              <Button variant="outline-primary" onClick={() => navigate(`/priceQuote/${quoteId}/updateLaborPricing`)}>
                Update Labor Pricing
              </Button>
            )}
            <Button className="m-0" variant="success" onClick={() => setShowAddLaborCode(true)}>
              Add Labor Codes
            </Button>
          </div>
        )}
      </div>
      {showAddLaborCode && (
        <AddLaborCodeForm
          laborCodes={laborCodes.map((item) => item.laborCode)}
          handleLaborCodeLookup={setShowAddLaborCode}
        />
      )}
      <div className="mb-5 container-fluid search-table">
        <div className="d-block d-md-none table-responsive customer-search__table ">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(event) => {
              const { active, over } = event;
              if (!over) return;
              const oldIndex = sorted.findIndex((item) => item.rank === active.id);
              const newIndex = sorted.findIndex((item) => item.rank === over.id);
              if (oldIndex !== newIndex) {
                toast.promise(
                  Promise.all(
                    arrayMove(sorted, oldIndex, newIndex).map((lc, index) =>
                      updateLaborCode({ laborCodeId: lc.laborCodeId, floorId, rank: index + 1 }).unwrap(),
                    ),
                  ),
                  { success: "Labor order saved", pending: "Updating labor order", error: "Something went wrong" },
                );
              }
            }}
          >
            <table id="example" className="table table-root-floor">
              <thead className="table-root__header">
                <tr className="table-root__header__tr">
                  {(canEdit ? laborItemsHeadersEditable : laborItemsHeaders).map(({ id, name, className }) =>
                    name === "Checkbox" ? (
                      <th
                        key={id}
                        className="table-root-floor__body__tr__td table-root-floor__body__tr__td__checkbox product"
                      >
                        <input
                          type="checkbox"
                          aria-label="select all"
                          className="table-root__header__tr__th__checkbox--input"
                          checked={allSelected}
                          onChange={handleSelectAllLabor}
                          disabled={!canEdit}
                        />
                      </th>
                    ) : (
                      <th key={id} className={`table-root__header__tr__th table-root__body__tr__td__text ${className}`}>
                        <div>{name}</div>
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <SortableContext items={sorted.map((l) => l.rank)} strategy={verticalListSortingStrategy}>
                <tbody className="table-root-floor__body">
                  {sorted.length > 0 || isFetchingLaborCodes ? (
                    sorted.map((row) => <DraggableLaborRow key={row.laborCodeId} row={row} />)
                  ) : (
                    <tr>
                      <td colSpan={12} className="no-results--text text-center">
                        Zero results were found based on your search criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </SortableContext>
            </table>
          </DndContext>
        </div>
      </div>
    </>
  );
};

export default QuoteFloorLabor;
